/**
 * Demo Service - Handles demo mode functionality
 * Provides API calls for demo account and anonymous photo testing
 * Supports multiple demo accounts for different audiences
 */

import { directApi } from './api';

const DEMO_STORAGE_KEYS = {
  IS_DEMO: 'demo_mode',
  DEMO_INFO: 'demo_info',
  DEMO_TYPE: 'demo_type'
};

// Available demo account types
export const DEMO_TYPES = {
  DEFAULT: 'default',
  DEMO_A: 'demo-a',      // Men - Garage & Workshop
  DEMO_B: 'demo-b',      // Women - Home & Crafts
  HOME: 'demo-home',     // Homeowners
  MOVING: 'demo-moving', // Moving
  COLLECTOR: 'demo-collector', // Collectors
  BUSINESS: 'demo-business'    // Small Business
};

export const DEMO_TYPE_LABELS = {
  'default': 'General Demo',
  'demo-a': 'Garage & Workshop',
  'demo-b': 'Home & Crafts', 
  'demo-home': 'Homeowner',
  'demo-moving': 'Moving',
  'demo-collector': 'Collector',
  'demo-business': 'Small Business'
};

/**
 * Check if user is in demo mode
 */
export const isDemoMode = () => {
  return localStorage.getItem(DEMO_STORAGE_KEYS.IS_DEMO) === 'true';
};

/**
 * Get demo info (item limit, features to try, etc.)
 */
export const getDemoInfo = () => {
  try {
    const info = localStorage.getItem(DEMO_STORAGE_KEYS.DEMO_INFO);
    return info ? JSON.parse(info) : null;
  } catch {
    return null;
  }
};

/**
 * Clear demo mode data
 */
export const clearDemoMode = () => {
  localStorage.removeItem(DEMO_STORAGE_KEYS.IS_DEMO);
  localStorage.removeItem(DEMO_STORAGE_KEYS.DEMO_INFO);
  localStorage.removeItem(DEMO_STORAGE_KEYS.DEMO_TYPE);
};

/**
 * Get current demo type
 */
export const getDemoType = () => {
  return localStorage.getItem(DEMO_STORAGE_KEYS.DEMO_TYPE) || 'default';
};

/**
 * Start demo mode with a specific demo type
 * @param {string} demoType - One of DEMO_TYPES values
 * @returns {Promise<object>} - Demo login response with token and user info
 */
export const startDemo = async (demoType = 'default') => {
  try {
    const response = await directApi.post('/demo/login', { demo_type: demoType });
    
    if (response.data.success) {
      // Store demo mode info
      localStorage.setItem(DEMO_STORAGE_KEYS.IS_DEMO, 'true');
      localStorage.setItem(DEMO_STORAGE_KEYS.DEMO_TYPE, demoType);
      localStorage.setItem(DEMO_STORAGE_KEYS.DEMO_INFO, JSON.stringify(response.data.demo_info));
      
      console.log(`[Demo] Started demo mode: ${demoType}`);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error starting demo:', error);
    throw error;
  }
};

/**
 * Get demo account status and limits
 */
export const getDemoStatus = async () => {
  try {
    const response = await directApi.get('/demo/status');
    return response.data;
  } catch (error) {
    console.error('Error getting demo status:', error);
    throw error;
  }
};

/**
 * Reset demo account to initial state
 */
export const resetDemoAccount = async () => {
  try {
    const response = await directApi.post('/demo/reset');
    
    // Update local demo info
    if (response.data.success) {
      const currentInfo = getDemoInfo();
      if (currentInfo) {
        currentInfo.current_items = response.data.current_items;
        currentInfo.current_boxes = response.data.current_boxes;
        // Reset all feature completion status
        if (currentInfo.features_to_try) {
          currentInfo.features_to_try.forEach(feature => {
            feature.completed = false;
          });
        }
        localStorage.setItem(DEMO_STORAGE_KEYS.DEMO_INFO, JSON.stringify(currentInfo));
        console.log('[Demo] Reset complete - all features marked as incomplete');
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error resetting demo account:', error);
    throw error;
  }
};

/**
 * Mark a demo feature as completed
 */
export const markFeatureCompleted = (featureId) => {
  const info = getDemoInfo();
  if (!info || !info.features_to_try) return;
  
  const feature = info.features_to_try.find(f => f.id === featureId);
  if (feature && !feature.completed) {
    feature.completed = true;
    localStorage.setItem(DEMO_STORAGE_KEYS.DEMO_INFO, JSON.stringify(info));
    // Dispatch event to notify DemoGuide component
    window.dispatchEvent(new CustomEvent('demoFeatureCompleted', { detail: { featureId } }));
    console.log(`[Demo] Feature completed: ${featureId}`);
  }
};

/**
 * Get completion progress for demo features
 */
export const getDemoProgress = () => {
  const info = getDemoInfo();
  if (!info || !info.features_to_try) {
    return { completed: 0, total: 0, percentage: 0 };
  }
  
  const completed = info.features_to_try.filter(f => f.completed).length;
  const total = info.features_to_try.length;
  
  return {
    completed,
    total,
    percentage: Math.round((completed / total) * 100)
  };
};

/**
 * Check if demo user can add more items
 */
export const canAddMoreItems = () => {
  const info = getDemoInfo();
  if (!info) return true; // Not in demo mode
  
  return info.current_items < info.item_limit;
};

/**
 * Get remaining item slots for demo user
 */
export const getRemainingItemSlots = () => {
  const info = getDemoInfo();
  if (!info) return null; // Not in demo mode
  
  return Math.max(0, info.item_limit - info.current_items);
};

/**
 * Update demo item count after adding/removing items
 */
export const updateDemoItemCount = (delta) => {
  const info = getDemoInfo();
  if (!info) return;
  
  info.current_items = Math.max(0, (info.current_items || 0) + delta);
  localStorage.setItem(DEMO_STORAGE_KEYS.DEMO_INFO, JSON.stringify(info));
};

export default {
  isDemoMode,
  getDemoInfo,
  getDemoType,
  clearDemoMode,
  startDemo,
  getDemoStatus,
  resetDemoAccount,
  markFeatureCompleted,
  getDemoProgress,
  canAddMoreItems,
  getRemainingItemSlots,
  updateDemoItemCount,
  DEMO_TYPES,
  DEMO_TYPE_LABELS
};
